import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular/lazy';

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private static readonly COOLDOWN_WINDOW_MS = 2000;

  private isToastOpen = false;
  private currentMessage: string | null = null;
  private messageQueue: { message: string; type: 'success' | 'error' }[] = [];
  private lastShownAt = new Map<string, number>();

  constructor(private toastController: ToastController) {}

  async successEvent(message: string) {
    if (!this.isDuplicate(message)) {
      this.messageQueue.push({ message, type: 'success' });
      await this.processQueue();
    }
  }

  async errorEvent(message: string) {
    if (!this.isDuplicate(message)) {
      this.messageQueue.push({ message, type: 'error' });
      await this.processQueue();
    }
  }

  private isDuplicate(message: string): boolean {
    if (this.currentMessage === message) return true;
    if (this.messageQueue.some((item) => item.message === message)) return true;

    const lastShown = this.lastShownAt.get(message);
    return lastShown !== undefined && Date.now() - lastShown < AlertService.COOLDOWN_WINDOW_MS;
  }

  async dismissCurrent(): Promise<void> {
    if (this.isToastOpen) {
      await this.toastController.dismiss();
    }
  }

  private async processQueue() {
    if (!this.isToastOpen && this.messageQueue.length > 0) {
      const nextItem = this.messageQueue.shift();
      if (nextItem) {
        await this.showEvent(nextItem);
      }
    }
  }

  async showEvent(item: { message: string; type: 'success' | 'error' }) {
    if (this.isToastOpen) return;

    this.isToastOpen = true;
    this.currentMessage = item.message;

    const bottomTabBar = document.getElementById('tabs-bottom-bar');
    const hasVisibleBottomTabBar =
      !!bottomTabBar && bottomTabBar.getClientRects().length > 0;

    try {
      const toast = await this.toastController.create({
        message: item.message,
        duration: item.type === 'success' ? 3000 : 5000,
        position: 'bottom',
        ...(hasVisibleBottomTabBar
          ? { positionAnchor: bottomTabBar }
          : {}),
        color: item.type === 'success' ? 'toast-success' : 'toast-error',
      });

      toast.onDidDismiss().then(() => {
        this.isToastOpen = false;
        this.lastShownAt.set(item.message, Date.now());
        this.currentMessage = null;
        this.processQueue();
      });

      await toast.present();
    } catch {
      // Reset state so one failed toast doesn't block every later one.
      this.isToastOpen = false;
      this.currentMessage = null;
      await this.processQueue();
    }
  }
}
